/**
 * The renderer: locked orthographic camera, procedural terrain, entity
 * instances, and the pixel pipeline (KTD2).
 *
 * Owns the single `screenToWorld` / `worldToScreen` pair — see `viewport.ts`.
 * Reads simulation state and never writes it.
 */

import {
  AmbientLight,
  BufferAttribute,
  BufferGeometry,
  Color,
  DirectionalLight,
  InstancedMesh,
  LineSegments,
  LineDashedMaterial,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  NearestFilter,
  PCFSoftShadowMap,
  OrthographicCamera,
  Scene,
  ShaderChunk,
  Vector3,
  WebGLRenderer,
  WebGLRenderTarget,
  Quaternion,
} from 'three'
import type { Ctx, Subsystem } from '../core/registry.js'
import * as fix from '../core/fixed.js'
import { INTERNAL_WIDTH, INTERNAL_HEIGHT, PIXEL_SCALE } from '../core/config.js'
import { generatePalette, type Palette } from '../forge/palette.js'
import { Entities, EF } from '../sim/entities.js'
import { World } from '../sim/world.js'
import type { Vision } from '../sim/vision.js'
import { defById, ENTITY_DEFS, TAB } from '../sim/data/entities.js'
import { weaponById, PROJECTILE } from '../sim/data/weapons.js'
import { WARHEAD } from '../sim/data/armor.js'
import { recipeFor } from '../forge/recipes.js'
import { previewGeometries } from '../forge/preview.js'
import { PixelPass } from './pixel.js'
import { buildTerrainGeometry, buildSurfaceHeights, surfaceHeightAt, LEVEL_STEP, WATER_DROP, WATER_SWELL } from './terrain.js'
import { buildScatterGeometry } from './scatter.js'
import { OreField } from './ore.js'
import { Effects } from './effects.js'
import { Fog } from './fog.js'
import { Bars } from './bars.js'
import { EntityRenderer, AIR_ALTITUDE, scaleFor } from './entities.js'
import { TransformBuffer } from './interp.js'
import {
  CameraBasis,
  computeLayout,
  screenToWorld as screenToWorldMath,
  worldToScreen as worldToScreenMath,
  projectOnSurface,
  targetForPan,
  clampPan,
  type CameraState,
  type Layout,
} from './viewport.js'

export const SIDEBAR_WIDTH = 208

/** Radians of water swell per simulation tick. A full period every ~8 seconds. */
const SWELL_RATE = 0.013

/** Ticks between checks for ore having been mined away. */
const ORE_SYNC_TICKS = 15

/**
 * Which accent ramp an energy weapon's beam is drawn from.
 *
 * By slot rather than by faction, so a four-player game does not give two
 * players the same laser. Falls back to the warm accent for anything unowned.
 */
function beamAccent(owner: number): string {
  return owner === 0 || owner === 2 ? 'helios' : owner >= 0 ? 'ascendancy' : 'fire'
}

/**
 * The colour ramp an impact burns through, keyed by the weapon that caused it.
 *
 * A chemical explosion and a focused-light hit are different events and have to
 * look like it — a prism strike goes white to cyan to violet, a shell goes white
 * to orange to smoke. `weapon:impact` carries no owner, so the accent comes from
 * the weapon's own key rather than from the shooter's slot: an Ascendancy lance
 * is violet whoever is holding it, which is the correct reading anyway.
 */
const IMPACT_RAMPS: Record<string, string> = {
  'prism-beam': 'energy:helios',
  'mirror-lance': 'energy:helios',
  'lance-arc': 'energy:ascendancy',
  'skirmish-arc': 'energy:ascendancy',
  'wraith-lance': 'energy:ascendancy',
  'logic-bomb': 'energy:ascendancy',
  'arc-pylon': 'energy:ascendancy',
}

/**
 * How large a blast a weapon leaves, from its splash radius.
 *
 * Ordnance used to land at the same 1.6 as a mortar round, which is why an air
 * strike — the most dramatic thing that happens in a match — was the least
 * visible. Reading the real splash figure means the scale of the effect tracks
 * the scale of the damage, and the superweapons at 4.5 and 5 cells get an
 * explosion to match instead of a puff.
 */
function blastScale(weapon: { splash: fix.Fixed } | undefined): number {
  if (!weapon) return 0.8
  const splash = fix.toFloat(weapon.splash)
  if (splash <= 0) return 0.7
  return Math.min(3.6, 1.1 + splash * 0.55)
}

/**
 * What the renderer needs from combat: where the shells are. Declared here
 * rather than imported, so the render layer does not depend on the simulation's
 * combat module — it reads a shape, not a class.
 */
interface CombatView {
  readonly projectileCapacity: number
  projectileAt(index: number): { x: number; y: number; active: boolean }
}

/** A footprint preview under the cursor during placement mode. */
export interface PlacementGhost {
  cx: number
  cy: number
  w: number
  h: number
  legal: boolean
}

/** Daylight from the left, with enough lateral offset to reveal cast shadows. */
const SUN_DIRECTION = new Vector3(-0.55, 0.8, 0.5).normalize()

const SUN_INTENSITY = 2.5

/**
 * The up vector three uses when it aims the shadow camera. Matching it here is
 * what makes the texel snap land on the same axes the shadow map is rasterised
 * on; any other up would snap to a grid the map does not use.
 */
const SHADOW_UP = new Vector3(0, 1, 0)

/** Keep ground shadows tonal so scenery does not hide moving units. */
const GROUND_SHADOW = 0.42

/** Fill shaded terrain and scenery faces without flattening their silhouettes. */
const GROUND_SHADE_FLOOR = 0.32

/** Retain readable side planes on the layered cliffs. */
const GROUND_SIDE_TIER = 0.9

/** Multisample count for the world target. Four is the usual sweet spot. */
const MSAA_SAMPLES = 4

const MAX_INSTANCES = 2048



export class Renderer implements Subsystem {
  static id = 'render'
  static deps = ['world', 'entities'] as const

  readonly palette: Palette
  /**
   * Rebuilt on resize: the internal target now follows the viewport, so the
   * frustum and the world-per-pixel steps follow it too.
   */
  basis = new CameraBasis()
  readonly camera: CameraState = { panX: 0, panY: 0 }

  private ctx!: Ctx
  private world!: World
  private entities!: Entities

  private renderer!: WebGLRenderer
  private scene = new Scene()
  private orthoCamera!: OrthographicCamera
  private target!: WebGLRenderTarget
  private pixel!: PixelPass
  private transforms = new TransformBuffer()

  private terrainMesh: Mesh | null = null
  private scatterMesh: Mesh<BufferGeometry, MeshLambertMaterial> | null = null
  private ore: OreField | null = null
  /** Drives the water swell. Seconds, wrapped so the float never gets coarse. */
  private readonly waterTime = { value: 0 }
  private entityRenderer: EntityRenderer | null = null
  private sun: DirectionalLight | null = null
  /** Scratch for the light-space basis the shadow snap is computed in. */
  private readonly shadowX = new Vector3()
  private readonly shadowY = new Vector3()
  private readonly shadowZ = new Vector3()
  private readonly shadowCentre = new Vector3()
  /** Selection rings. Client-side only — never part of simulation state. */
  private ringMesh: InstancedMesh | null = null
  private markerMesh: InstancedMesh | null = null
  private ghostMesh: InstancedMesh | null = null
  private effects: Effects | null = null
  readonly fog = new Fog()
  private bars: Bars | null = null
  private ghost: PlacementGhost | null = null
  private highlighted = new Set<number>()
  private markerSource: { markers: ReadonlyArray<{ x: number; z: number; kind: string }> } | null =
    null

  private surface = new Float32Array(0)
  private layout: Layout = computeLayout(1280, 720, SIDEBAR_WIDTH, 1)
  private readonly scratchVec = new Vector3()
  private readonly scratchScale = new Vector3()
  private routeMesh: LineSegments | null = null
  private readonly routeCells = new Int32Array(128)
  private readonly routePositions = new Float32Array(768 * 6)
  private readonly routeDistances = new Float32Array(768 * 2)
  private readonly scratchMatrix = new Matrix4()
  private readonly scratchColor = new Color()

  constructor(
    private readonly canvas: HTMLCanvasElement,
    paletteSeed: number | string = 'blue-alert',
    private readonly hasHud = true,
  ) {
    this.palette = generatePalette(paletteSeed)
  }

  init(ctx: Ctx): void {
    this.ctx = ctx
    this.world = ctx.get<World>('world')
    this.entities = ctx.get<Entities>('entities')

    this.renderer = new WebGLRenderer({
      canvas: this.canvas,
      // The world is drawn into a multisampled render target, not straight to
      // the canvas, so this flag would cost a second AA pass on the blit alone.
      antialias: false,
      alpha: false,
      powerPreference: 'high-performance',
    })
    this.renderer.setPixelRatio(1)
    this.renderer.setClearColor(0x0a0705, 1)
    this.renderer.shadowMap.enabled = true
    // Filtered, not hard-edged.
    //
    // An unfiltered shadow map takes one sample per pixel, so a shadow edge
    // lands wholly in or wholly out and the result crawls with speckle. Worse
    // here than usual: these models are assembled from separate boxes with real
    // daylight between them, and every one of those gaps punched a hole through
    // the shadow. Filtering averages neighbouring samples, which softens the
    // edge and closes the gaps in one move — the same thing a physically
    // broader light source would do, at a fraction of the cost of a second
    // shadow-casting light.
    this.renderer.shadowMap.type = PCFSoftShadowMap

    // Multisampling, which this deliberately did without for a long time.
    //
    // The original reasoning: an antialiased edge is a blend of the two colours
    // either side of it, a blend of two palette colours is not itself a palette
    // colour, and a scene made of small flat quads is nearly all edges. That
    // held while a quantizer snapped every pixel back onto the palette and the
    // internal target was a chunky 480x270 upscale — under those conditions
    // antialiasing was wasted work that only muddied the ramp.
    //
    // Neither is true now. The quantizer is off by default and the render is
    // native, so what the player actually sees is the raw geometry edge, and at
    // this zoom a stair-stepped diagonal on every hull and cliff is the most
    // obvious flaw on screen. The colour-discipline check is chromatic rather
    // than exact-match, so an edge blending two palette colours still points
    // the same direction in RGB space and still passes.
    this.target = new WebGLRenderTarget(INTERNAL_WIDTH, INTERNAL_HEIGHT, {
      minFilter: NearestFilter,
      magFilter: NearestFilter,
      depthBuffer: true,
      generateMipmaps: false,
      samples: MSAA_SAMPLES,
    })

    this.orthoCamera = new OrthographicCamera(
      -this.basis.halfWidth,
      this.basis.halfWidth,
      this.basis.halfHeight,
      -this.basis.halfHeight,
      0.1,
      600,
    )
    this.pixelScale = ctx.config.pixelScale

    this.pixel = new PixelPass(this.palette)
    this.pixel.setInput(this.target.texture)
    this.pixel.setEnabled(ctx.config.quantize)

    // Unexplored ground is painted a flat colour rather than multiplied toward
    // black, and that colour has to come from the palette like every other pixel
    // on screen. It is the *darkest ground material* at its darkest step, lifted
    // just clear of the range the cliff shadows occupy — a uniform scale of a
    // palette colour is still that colour, so it costs nothing on the
    // colour-discipline metric.
    //
    // It used to be the brine's darkest step, back when that was a slate teal,
    // and the void behind the map came out as a cool slab that belonged to no
    // other part of the frame. A near-black of the terrain's own hue reads as
    // ground nobody has walked on yet rather than as a hole cut in the picture.
    //
    // **It is the neutral ramp's dark step now, not the basalt one.** Painting
    // the void in the terrain's own hue was the fix for a slate-teal shroud that
    // belonged to no other part of the frame, and it over-corrected: at
    // (50, 27, 20) the unexplored ground was a dead near-black brown one step off
    // the cliff shadows it abutted, so the largest single mass in
    // `fog-boundary.png` was also the one with the least information in it, and it
    // shared a hue with the map it was supposed to be hiding. A near-neutral at
    // twice the value is unknown *atmosphere*: it is desaturated rather than
    // recoloured, it sits well clear of the shadow band so a shadow no longer
    // reads as unexplored territory, and against a basin now running at 0.88
    // display saturation the eye supplies the coolness on its own without a second
    // hue family being introduced to the palette to do it.
    this.fog.shroudColor
      .setRGB(...this.palette.rgb(this.palette.neutralRamp.indices[1]))
      .multiplyScalar(0.95 / 255)
    this.buildLights()
    this.buildTerrain()
    this.buildEntityMeshes()
    this.effects = new Effects(this.scene, this.palette, ctx.config.quality.maxParticles)
    this.bars = new Bars(this.scene, this.palette)
    this.bars.groundAt = (x, z) => this.groundAt(x, z)
    this.entityRenderer?.attachFog(this.fog)
    this.effects.attachFog(this.fog)
    if (ctx.has('vision')) {
      const vision = ctx.get<Vision>('vision')
      this.entityRenderer!.vision = vision
      this.bars!.visible = (slot) => this.entityRenderer!.canSeeSlot(slot)
      this.effects.visibleAt = (x, z) => this.fog.localSlot < 0 ||
        vision.isVisibleAt(this.fog.localSlot, Math.floor(x), Math.floor(z))
    }
    this.subscribeEffects(ctx)

    this.resize(window.innerWidth, window.innerHeight)
    this.centerOn(this.world.size / 2, this.world.size / 2)
  }

  // --- construction --------------------------------------------------------

  private buildLights(): void {
    // Two lights, both fixed. A moving light would make the quantizer's output
    // shift under a pan, which is the same crawl problem in another guise.
    // Angled well off vertical so cliff tops, north walls, and east walls all
    // land on different ramp steps. A near-vertical sun flattens the relief.
    //
    // The intensities look large because Three's lighting has been physically
    // correct since r155: a Lambert surface reflects `NdotL * intensity / PI`,
    // so an intensity of 1 renders a white surface at roughly a third of its
    // albedo. These values put a lit top face at ~1.0 of its palette colour.
    //
    // **Both lights are neutral**, and that is a deliberate constraint rather
    // than an oversight. A tinted light multiplies each channel by a different
    // amount, so a rendered pixel is no longer its palette colour at some
    // brightness — it is a colour that appears nowhere in the palette. All the
    // warmth belongs in the palette, where it is authored once and stays
    // checkable: the critic's colour-discipline metric asserts that every pixel
    // on screen still points at a palette entry, and a coloured light is
    // indistinguishable from a colour that leaked in from somewhere else.
    // Nothing may clip. A channel that saturates at 255 while its neighbours
    // do not is a colour the palette never contained — the brightest ore came
    // back as (255, 255, 71), a yellow that exists nowhere in the ramp it was
    // drawn from. These intensities put the brightest lit face at ~0.96 of its
    // albedo, just under the ceiling.
    // The sun is off the camera axis, which matters more than it sounds.
    //
    // It used to sit at (0.55, 0.72, 0.28) — almost exactly where the camera
    // is. That lights every face the camera can see, which is why the terrain
    // read as evenly bright, and it throws every shadow directly *behind* the
    // object casting it, where the object hides it. Turning shadow mapping on
    // with the light in that position changed nothing visible on the ground,
    // and it took a floating test cube to notice the shadows had been there all
    // along, tucked out of sight.
    //
    // Now it comes over the player's right shoulder, so shadows fall to the
    // lower left the way this genre has always drawn them. The trade is that
    // south-facing cliff walls no longer catch direct light — they sit on
    // ambient alone, which is what gives the relief its contrast.
    const sun = new DirectionalLight(0xffffff, SUN_INTENSITY)
    sun.position.copy(SUN_DIRECTION)

    // Real shadows, cast by the geometry itself.
    //
    // The alternative — a dark quad under each unit — was what this had, and it
    // cannot be made right. It has to be pushed clear of the hull to be visible
    // at all, and something standing on the ground with a shadow beside it
    // reads as hovering. It also knows nothing about the object it belongs to:
    // an infantry squad is three figures and got one rectangle.
    //
    // A shadow map has none of those problems because it is not an
    // approximation of a shadow, it is the actual occlusion: three figures cast
    // three shadows, a tank's shadow touches its treads, a hovercraft's does
    // not, and a gun barrel puts a line across the ground behind it. It costs a
    // second pass over the scene, which at this triangle count is affordable.
    sun.castShadow = true
    sun.shadow.mapSize.set(2048, 2048)
    // A tight frustum around the visible area, repositioned as the camera pans
    // in `syncShadowCamera`. Sized to the map would waste almost all of the
    // texels on ground nobody is looking at.
    const shadow = sun.shadow.camera
    // Fitted to the depth the scene actually occupies, not to a safe-looking
    // round number. `shadow.bias` is measured in the shadow camera's clip
    // space, so its cost in world units is proportional to `far - near`: at
    // 1..160 a bias of -0.0015 slid every shadow about an eighth of a cell
    // away from the thing casting it. That is peter-panning, and it is what
    // detached a tank's shadow from its treads and bit a notch out of it where
    // the shadows of two separate parts no longer met.
    //
    // The light sits 60 back along its own direction, the visible ground is
    // within `reach` of the target, and nothing on the map stands taller than
    // a few units — so the whole scene lives in a 60-unit slab around that.
    shadow.near = 12
    shadow.far = 108
    // Small enough now that the contact point stays put. The residual acne
    // risk on the one enormous flat surface is handled by `normalBias`, which
    // offsets along the surface normal instead and therefore does not move a
    // shadow sideways at all.
    sun.shadow.bias = -0.0002
    sun.shadow.normalBias = 0.035
    this.sun = sun
    this.scene.add(sun)
    this.scene.add(sun.target)
    // Ambient carries everything the sun cannot reach, so it also sets how dark
    // a shadow gets. Lifting it to soften shadows on dark rock was tried and
    // reverted: it costs entity/terrain contrast at both ends, dimming the lit
    // ground and lifting the units' own shaded faces, and the readability of a
    // unit against the ground it is standing on is worth more than the depth
    // of a boulder's shadow.
    this.scene.add(new AmbientLight(0xffffff, 1.2))
  }

  /**
   * Ground height at a world position. Everything that sits on the map reads
   * it from here — units, selection rings, the placement ghost — so they cannot
   * disagree with the terrain about where the surface is.
   */
  groundAt(x: number, z: number): number {
    return surfaceHeightAt(this.world, this.surface, x, z)
  }

  private surfaceCeiling = LEVEL_STEP
  private readonly sampleGround = (x: number, z: number): number => this.groundAt(x, z)

  private buildTerrain(): void {
    this.surface = buildSurfaceHeights(this.world)
    this.surfaceCeiling = this.surface.reduce((max, h) => Math.max(max, h), 0) + 0.1
    if (this.entityRenderer) this.entityRenderer.groundAt = (x, z) => this.groundAt(x, z)
    if (this.effects) this.effects.groundAt = (x, z) => this.groundAt(x, z)
    const data = buildTerrainGeometry(this.world, this.palette)
    const geometry = new BufferGeometry()
    geometry.setAttribute('position', new BufferAttribute(data.positions, 3))
    geometry.setAttribute('normal', new BufferAttribute(data.normals, 3))
    geometry.setAttribute('color', new BufferAttribute(data.colors, 3))
    geometry.setAttribute('wave', new BufferAttribute(data.waves, 1))
    geometry.setIndex(new BufferAttribute(data.indices, 1))
    geometry.computeBoundingSphere()

    // No texture on the ground.
    //
    // A per-terrain detail atlas was tried and removed: at this zoom it read as
    // dirt on the lens rather than as material, and it fought the one thing the
    // blocky direction has going for it — flat, unambiguous colour that the eye
    // can read terrain type from instantly. What gives the ground its substance
    // now is relief and real shadow, not surface noise.
    const material = new MeshLambertMaterial({ vertexColors: true, flatShading: true })
    this.fog.attach(material, true)
    this.attachSwell(material)
    this.softenGroundShadow(material)
    this.liftGroundShade(material, 'terrain-ground')
    this.terrainMesh = new Mesh(geometry, material)
    // Terrain casts as well as receives.
    //
    // It used to only receive, on the grounds that the map is a single mesh and
    // casting means re-drawing every triangle of it into the shadow map for one
    // effect. That effect turns out to be the whole reason the reference frames
    // read as tall: a mesa with a long shadow lying off its foot is anchored in
    // three dimensions, and the same mesa without one is a coloured region you
    // cannot tell from a stain. Nothing else available here says "this mass has
    // height" as cheaply, and the cost is one extra depth-only draw call of
    // static geometry.
    this.terrainMesh.castShadow = true
    this.terrainMesh.receiveShadow = true
    this.terrainMesh.frustumCulled = false
    this.scene.add(this.terrainMesh)
    this.buildScatter()

    // Ore is the one part of the map that is consumed, so it cannot live in
    // the meshes baked here. It gets its own, driven by what is left.
    this.ore ??= new OreField(this.scene, this.palette)
    this.ore.groundAt = (x, z) => this.groundAt(x, z)
    this.ore.build(this.world, (m) => this.fog.attach(m))
  }

  /**
   * Boulders, ore crystals and grass, as one static mesh.
   *
   * Separate from the terrain because it is the one part of the map that
   * casts: the ground is a single mesh and re-drawing every triangle of it
   * into the shadow map to gain a few cliff shadows is not worth it, while
   * scatter is a few thousand small boxes whose whole purpose is to have a
   * shadow beside them.
   */
  private buildScatter(): void {
    if (this.scatterMesh) {
      this.scene.remove(this.scatterMesh)
      this.scatterMesh.geometry.dispose()
      this.scatterMesh.material.dispose()
    }
    const data = buildScatterGeometry(this.world)
    if (data.indices.length === 0) {
      this.scatterMesh = null
      return
    }
    const geometry = new BufferGeometry()
    geometry.setAttribute('position', new BufferAttribute(data.positions, 3))
    geometry.setAttribute('normal', new BufferAttribute(data.normals, 3))
    geometry.setAttribute('color', new BufferAttribute(data.colors, 3))
    geometry.setIndex(new BufferAttribute(data.indices, 1))
    geometry.computeBoundingSphere()

    const material = new MeshLambertMaterial({ vertexColors: true, flatShading: true })
    this.fog.attach(material, true)
    // Scatter is ground, so it takes the same two treatments the ground does. A
    // pebble is a few pixels across and two of its three visible faces point
    // away from the sun; a chip lying in the shadow of the cliff it fell off
    // gets neither the sun nor an unshadowed ambient. Without both, every prop
    // in a shaded pocket reads as one dark speck rather than as a small object,
    // which is the whole reason the scatter is there.
    this.softenGroundShadow(material)
    this.liftGroundShade(material, 'ground-scatter')
    this.scatterMesh = new Mesh(geometry, material)
    this.scatterMesh.castShadow = true
    this.scatterMesh.receiveShadow = true
    this.scatterMesh.frustumCulled = false
    this.scene.add(this.scatterMesh)
  }

  /**
   * Lift water vertices on a slow swell.
   *
   * Until now nothing on the map moved unless a player ordered it to, and a
   * scene where only the units animate reads as a diorama with toys being
   * pushed around it. Open water is the right place to fix that: it is the one
   * surface that is *expected* to move, and it costs a vertex shader.
   *
   * The displacement is quantized to a few discrete levels rather than left
   * smooth, so the surface steps between them the way the rest of the map is
   * stepped. A smooth sine here looked like cloth.
   *
   * Vertices are not shared between quads, so two quads meeting at an edge
   * hold coincident copies of the same corner — and since the wave is a pure
   * function of world position, both copies move by exactly the same amount
   * and the seam cannot open. Flat shading derives its normal per fragment, so
   * the lighting follows the displacement for free.
   */
  private attachSwell(material: MeshLambertMaterial): void {
    const previous = material.onBeforeCompile.bind(material)
    material.onBeforeCompile = (shader, renderer) => {
      previous(shader, renderer)
      shader.uniforms.uSwell = this.waterTime
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
attribute float wave;
uniform float uSwell;`,
        )
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
if (wave > 0.5) {
  float s = sin(position.x * 0.9 + uSwell) + sin(position.z * 0.75 - uSwell * 0.8);
  transformed.y += floor(s * 1.5) * ${WATER_SWELL};
}`,
        )
    }
    material.customProgramCacheKey = () => 'swell'
  }

  /**
   * Make a shadow falling on the *ground* tonal rather than black.
   *
   * Ambient is 1.0 against a sun contributing 1.87, so a shadowed ground quad keeps only
   * about a third of the brightness of the lit quad beside it. On a unit that
   * ratio is exactly right — it is what makes a tank read as a solid object
   * sitting on the map. On the ground it is far too much: every boulder,
   * every rubble chip and every cliff-foot pile stamps a near-black hole into
   * the surface, and enough of them together read as high-frequency noise
   * rather than as lighting.
   *
   * Lifting ambient was the obvious fix and is the wrong one — it was tried and
   * reverted, because it dims the lit ground and lifts the units' shaded faces
   * at the same time, costing exactly the entity/terrain contrast that matters
   * most. Scaling the shadow *term* instead touches only surfaces receiving a
   * shadow, and only on this material: the sun still throws a shadow of the same
   * shape in the same place, it is just no longer the darkest thing in the frame.
   *
   * `shadowIntensity` is Three's own per-light mix between "fully lit" and "fully
   * occluded", so scaling it needs no new uniform and cannot desynchronise from
   * however the shadow itself is sampled.
   *
   * The `#include` has to be expanded by hand. `onBeforeCompile` runs *before*
   * Three resolves its chunk includes, so the shader handed to it still reads
   * `#include <lights_fragment_begin>` and a replace against anything inside that
   * chunk silently matches nothing — which is exactly what the first version of
   * this did. Substituting the chunk's own source for the directive puts the text
   * in front of us, and the assertion below is what keeps the failure loud if a
   * Three upgrade renames the uniform.
   */
  private softenGroundShadow(material: MeshLambertMaterial): void {
    const term = 'directionalLightShadow.shadowIntensity'
    const lit = ShaderChunk.lights_fragment_begin
    if (!lit.includes(term)) throw new Error(`three shadow term moved: ${term}`)
    const softened = lit.replace(term, `( ${term} * ${GROUND_SHADOW} )`)
    const previous = material.onBeforeCompile.bind(material)
    material.onBeforeCompile = (shader, renderer) => {
      previous(shader, renderer)
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <lights_fragment_begin>',
        softened,
      )
    }
    // A cache key of its own, because Three caches compiled programs by that key
    // and the scatter material is otherwise built from identical parameters —
    // sharing one hands this shader to a mesh that never asked for it. Both
    // ground materials take this patch now, and both then take
    // `liftGroundShade`, which sets the final distinguishing key.
    material.customProgramCacheKey = () => 'terrain-ground'
  }

  /**
   * Stop a ground face that is turned away from the sun going to near-black.
   *
   * `softenGroundShadow` fixes the *cast* shadow; this fixes the other half of
   * the same problem, which is the term before any shadow is sampled. The sun
   * comes over the player's right shoulder, so a south-facing cliff wall has a
   * `N·L` of zero and renders on ambient alone — about a third of the lit
   * plateau directly above it. On rock that lands near black, and the result was
   * the widest value range in the frame sitting on the *backdrop*: bone-pale
   * slab tops against black faces a few pixels away, with the army in between
   * having nowhere left to separate itself.
   *
   * Flooring `N·L` instead of lifting ambient is what keeps that local. A
   * higher ambient light brightens shaded faces everywhere, including a tank's,
   * which is exactly the contrast worth protecting; a floor inside
   * `RE_Direct_Lambert` reaches only the materials asked to take it. Faces that
   * already exceed the floor are untouched, so the lit end stays where it was
   * calibrated and nothing new clips — and a uniform scale of a palette colour
   * is still that colour, so colour discipline is unaffected.
   *
   * The second half is the *facing* tier — see `GROUND_SIDE_TIER`. `geometryNormal`
   * is in view space, and the camera is locked, so world up in view space is the
   * second column of `viewMatrix` and comparing against it costs one dot product.
   * Reading the geometry's normal *attribute* instead would not work: these
   * materials are flat-shaded, so the shader derives its normal from the winding
   * and the attribute is dead weight.
   *
   * Like the shadow patch, the `#include` has to be expanded by hand:
   * `onBeforeCompile` runs before Three resolves its chunks, so a replace
   * against text inside one silently matches nothing.
   */
  private liftGroundShade(material: MeshLambertMaterial, cacheKey: string): void {
    const term = 'float dotNL = saturate( dot( geometryNormal, directLight.direction ) );'
    const pars = ShaderChunk.lights_lambert_pars_fragment
    if (!pars.includes(term)) throw new Error('three lambert direct term moved')
    const floored = pars.replace(
      term,
      `${term}
	float groundUp = dot( geometryNormal, viewMatrix[ 1 ].xyz );
	dotNL *= mix( ${GROUND_SIDE_TIER}, 1.0, step( 0.5, groundUp ) );
	dotNL = max( dotNL, ${GROUND_SHADE_FLOOR} );`,
    )
    const previous = material.onBeforeCompile.bind(material)
    material.onBeforeCompile = (shader, renderer) => {
      previous(shader, renderer)
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <lights_lambert_pars_fragment>',
        floored,
      )
    }
    material.customProgramCacheKey = () => cacheKey
  }

  /**
   * Overlay meshes, and the entity renderer that owns everything else.
   *
   * Entities are drawn by `EntityRenderer` — one instanced mesh per kind per
   * rigid sub-part, so a turret can track independently of its hull.
   */
  private buildEntityMeshes(): void {
    this.entityRenderer = new EntityRenderer(this.scene, this.palette)
    this.entityRenderer.groundAt = (x, z) => this.groundAt(x, z)
    const route = new BufferGeometry()
    route.setAttribute('position', new BufferAttribute(this.routePositions, 3))
    route.setAttribute('lineDistance', new BufferAttribute(this.routeDistances, 1))
    route.setDrawRange(0, 0)
    this.routeMesh = new LineSegments(route, new LineDashedMaterial({
      color: 0x8ee7cf, transparent: true, opacity: 0.6, depthWrite: false,
      dashSize: 0.18, gapSize: 0.12,
    }))
    this.routeMesh.frustumCulled = false
    this.scene.add(this.routeMesh)

    // Open brackets preserve the silhouette and remain visible on slopes.
    const ringMat = new MeshBasicMaterial({
      transparent: true,
      opacity: 0.95,
      depthTest: false,
      depthWrite: false,
    })
    this.ringMesh = new InstancedMesh(selectionGeometry(), ringMat, MAX_INSTANCES)
    this.ringMesh.renderOrder = 5
    this.markerMesh = new InstancedMesh(diamondGeometry(0.5, 0.88), ringMat.clone(), 64)
    // The placement ghost: one flat tile per footprint cell, tinted per cell so
    // an illegal corner is visible rather than the whole footprint just failing.
    this.ghostMesh = new InstancedMesh(tileGeometry(0.92), ringMat.clone(), 64)

    for (const m of [this.ringMesh, this.markerMesh, this.ghostMesh]) {
      m.frustumCulled = false
      m.count = 0
      this.scene.add(m)
    }
  }

  /** Show or clear the placement footprint ghost. Client state; never hashed. */
  setPlacementGhost(ghost: PlacementGhost | null): void {
    this.ghost = ghost
  }

  /** Client-side selection highlight. Ids, so a dead unit drops out for free. */
  setHighlight(ids: readonly number[]): void {
    this.highlighted = new Set(ids)
  }

  /** Attach the order-marker source. Client state; never hashed. */
  setMarkerSource(source: { markers: ReadonlyArray<{ x: number; z: number; kind: string }> }): void {
    this.markerSource = source
  }

  // --- per-frame -----------------------------------------------------------

  /**
   * Runs after every simulation subsystem has stepped, so the captured
   * "current" transforms are the finished state of this tick and the previous
   * capture becomes "previous". Capturing inside `fixedUpdate` would race
   * whichever sim subsystems happen to sort after the renderer.
   */
  postFixedUpdate(tick: number): void {
    this.transforms.capture(this.entities)
    // Effects live on the simulation clock: aged here, once per tick, so that a
    // seek advances them instead of stacking every particle it ever spawned on
    // one frame. Aged before the tick's own emitters run, so what they spawn is
    // drawn fresh.
    this.effects?.fixedStep()
    this.emitTickEffects(tick)
  }

  update(): void {
    const alpha = this.ctx.time.alpha
    // Driven off the simulation clock rather than the wall clock, so a
    // captured shot is the same frame every time it is captured. Wrapped at a
    // whole number of periods so the float never grows coarse in a long game.
    this.waterTime.value = ((this.ctx.time.tick + alpha) * SWELL_RATE) % (Math.PI * 2)
    this.syncCamera()
    if (this.ctx.has('vision')) {
      this.fog.update(this.ctx.get<Vision>('vision'), this.orthoCamera.matrixWorld)
    }
    this.syncInstances(alpha)
    this.effects?.update(alpha)
    // Ore changes at the pace a harvester digs, so checking every frame is
    // wasted work. Twice a second is faster than the eye notices a crystal go.
    if (this.ctx.time.tick % ORE_SYNC_TICKS === 0) this.ore?.sync(this.world)

    this.renderer.setRenderTarget(this.target)
    this.renderer.clear()
    this.renderer.render(this.scene, this.orthoCamera)

    this.renderer.setRenderTarget(null)
    // Render-side clock only: the flicker must never reach the simulation.
    this.renderer.render(this.pixel.scene, this.pixel.camera)
  }

  private syncCamera(): void {
    const target = targetForPan(this.basis, this.camera)
    this.orthoCamera.position.copy(this.basis.positionFor(target))
    this.orthoCamera.lookAt(target)
    this.orthoCamera.updateMatrixWorld()
    this.syncShadowCamera(target)
  }

  /**
   * Keep the shadow frustum over what the player can actually see.
   *
   * A shadow map covering the whole map would spend nearly all its resolution
   * on ground off screen, and the shadows on screen would be a few texels wide.
   * Following the view instead keeps them sharp — at the cost of having to be
   * re-fitted whenever the camera moves or the window resizes.
   */
  private syncShadowCamera(target: Vector3): void {
    const sun = this.sun
    if (!sun) return

    // Half the visible diagonal, plus room for what is off screen but still
    // casting into it. Whole world units, so the frustum only changes size when
    // the window does.
    const reach = Math.ceil(Math.max(this.basis.halfWidth, this.basis.halfHeight) + 6)
    const shadow = sun.shadow.camera
    if (shadow.right !== reach) {
      shadow.left = -reach
      shadow.right = reach
      shadow.top = reach
      shadow.bottom = -reach
      shadow.updateProjectionMatrix()
    }

    // **Snap the frustum's centre to the shadow map's own texel grid.**
    //
    // Snapping `reach` was never enough, whatever the comment here used to claim.
    // The frustum kept its size but its *centre* was the raw view target, so as
    // the camera panned the texel grid slid continuously under a stationary world
    // and every shadow edge resampled against different texels each frame. That is
    // the shimmer `tools/critic.mjs` charges for in `panStability`, and it is why
    // `src/render/scatter.ts` places rubble per pile rather than per cell — it is
    // holding down the number of cells carrying a shadow edge to keep the metric
    // affordable, which is working around this rather than fixing it.
    //
    // The snap has to happen along the *light's* axes rather than the world's,
    // because those are the axes the shadow map is rasterised on. The basis is
    // built the way three builds it for the shadow camera: it looks from the light
    // toward the target, so its +Z is the direction back toward the light, and its
    // up is the camera's default (0, 1, 0).
    const texel = (2 * reach) / sun.shadow.mapSize.x
    const zAxis = this.shadowZ.copy(SUN_DIRECTION).normalize()
    const xAxis = this.shadowX.crossVectors(SHADOW_UP, zAxis).normalize()
    const yAxis = this.shadowY.crossVectors(zAxis, xAxis).normalize()

    const snapped = this.shadowCentre
      .copy(xAxis)
      .multiplyScalar(Math.round(target.dot(xAxis) / texel) * texel)
      .addScaledVector(yAxis, Math.round(target.dot(yAxis) / texel) * texel)
      // Depth is not rasterised into texels, so it does not need snapping and
      // rounding it would only jitter the near/far fit.
      .addScaledVector(zAxis, target.dot(zAxis))

    sun.target.position.copy(snapped)
    sun.target.updateMatrixWorld()
    // The light sits on its own direction, far enough back to clear the tallest
    // thing on the map.
    sun.position.copy(snapped).addScaledVector(SUN_DIRECTION, 60)
    sun.updateMatrixWorld()
  }

  private syncInstances(alpha: number): void {
    this.entityRenderer?.sync(this.entities, this.transforms, alpha, this.ctx.time.tick)
    if (this.bars) {
      this.bars.setFacing(this.orthoCamera.quaternion)
      this.bars.sync(this.entities, this.transforms, alpha)
    }
    this.syncOverlays(alpha)
  }

  private syncOverlays(alpha: number): void {
    if (!this.ringMesh || !this.markerMesh) return
    let rings = 0
    const limit = this.entities.limit
    for (let s = 0; s < limit && rings < this.ringMesh.instanceMatrix.count; s++) {
      if (!this.entities.isAlive(s)) continue
      if (!this.highlighted.has(this.entities.idAt(s))) continue
      const rx = this.transforms.x(s, alpha)
      const rz = this.transforms.y(s, alpha)
      this.scratchVec.set(rx, this.groundAt(rx, rz) + 0.03, rz)
      const def = defById(this.entities.kind[s])!
      this.scratchScale.set(def.isStructure ? def.width * 0.55 : 0.95, 1, def.isStructure ? def.height * 0.55 : 0.95)
      this.scratchMatrix.compose(this.scratchVec, IDENTITY_QUAT, this.scratchScale)
      this.ringMesh.setMatrixAt(rings, this.scratchMatrix)
      this.ringMesh.setColorAt(rings, this.scratchColor.setRGB(0.4, 0.9, 0.8))
      rings++
    }
    this.ringMesh.count = rings
    this.ringMesh.instanceMatrix.needsUpdate = true
    if (this.ringMesh.instanceColor) this.ringMesh.instanceColor.needsUpdate = true

    let markers = 0
    const list = this.markerSource?.markers ?? []
    for (const m of list) {
      if (markers >= this.markerMesh.instanceMatrix.count) break
      this.scratchVec.set(m.x, this.groundAt(m.x, m.z) + 0.04, m.z)
      this.scratchMatrix.compose(this.scratchVec, IDENTITY_QUAT, ONE_SCALE)
      this.markerMesh.setMatrixAt(markers, this.scratchMatrix)
      // Three states, not two. A rally point is a standing setting rather
      // than an acknowledged order, so it gets its own colour instead of
      // reading as a move order that never fades.
      if (m.kind === 'rally') this.scratchColor.setRGB(1, 0.85, 0.35)
      else if (m.kind === 'move') this.scratchColor.setRGB(0.55, 1, 0.6)
      else this.scratchColor.setRGB(1, 0.45, 0.3)
      this.markerMesh.setColorAt(markers, this.scratchColor)
      markers++
    }
    this.markerMesh.count = markers
    this.markerMesh.instanceMatrix.needsUpdate = true
    if (this.markerMesh.instanceColor) this.markerMesh.instanceColor.needsUpdate = true

    this.syncGhost()
    this.syncRoutes()
  }

  private syncRoutes(): void {
    if (!this.routeMesh) return
    const path = this.ctx.get<{ traceRoute(slot: number, out: Int32Array): number }>('pathfind')
    const vision = this.ctx.get<Vision>('vision')
    const player = this.fog.localSlot
    let segments = 0
    let routes = 0
    for (const id of this.highlighted) {
      const slot = this.entities.resolve(id)
      if (slot < 0) continue
      if (player >= 0 && this.entities.owner[slot] !== player) continue
      const count = path.traceRoute(slot, this.routeCells)
      if (count < 2) continue
      let distance = 0
      for (let i = 1; i < count && segments < 768; i++) {
        const a = this.routeCells[i - 1]
        const b = this.routeCells[i]
        if (player >= 0 && (!vision.isExplored(player, a) || !vision.isExplored(player, b))) break
        const ax = a % this.world.size + 0.5
        const az = Math.floor(a / this.world.size) + 0.5
        const bx = b % this.world.size + 0.5
        const bz = Math.floor(b / this.world.size) + 0.5
        const offset = segments * 6
        this.routePositions[offset] = ax
        this.routePositions[offset + 1] = this.groundAt(ax, az) + 0.07
        this.routePositions[offset + 2] = az
        this.routePositions[offset + 3] = bx
        this.routePositions[offset + 4] = this.groundAt(bx, bz) + 0.07
        this.routePositions[offset + 5] = bz
        this.routeDistances[segments * 2] = distance
        distance += Math.hypot(bx - ax, bz - az)
        this.routeDistances[segments * 2 + 1] = distance
        segments++
      }
      if (++routes === 4) break
    }
    this.routeMesh.geometry.setDrawRange(0, segments * 2)
    this.routeMesh.geometry.getAttribute('position').needsUpdate = true
    this.routeMesh.geometry.getAttribute('lineDistance').needsUpdate = true
  }

  private syncGhost(): void {
    if (!this.ghostMesh) return
    const g = this.ghost
    if (!g) {
      this.ghostMesh.count = 0
      return
    }
    let n = 0
    for (let dy = 0; dy < g.h && n < this.ghostMesh.instanceMatrix.count; dy++) {
      for (let dx = 0; dx < g.w && n < this.ghostMesh.instanceMatrix.count; dx++) {
        const cx = g.cx + dx
        const cy = g.cy + dy
        // An illegal placement never shows a green cell, whatever the reason.
        // Reading per-cell passability *instead* of the verdict was wrong: a
        // footprint outside the build area is blocked by nothing in particular,
        // so every cell tested clear and the whole ghost stayed green while the
        // placement was refused.
        //
        // Blocked cells still read darker than merely-illegal ones, so "this
        // corner is on a cliff" stays distinguishable from "this is too far
        // from your base".
        const blocked =
          !this.world.inBounds(cx, cy) ||
          !this.world.isPassable(cx, cy) ||
          this.world.isCliff(cx, cy)
        this.scratchVec.set(cx + 0.5, this.groundAt(cx + 0.5, cy + 0.5) + 0.05, cy + 0.5)
        this.scratchMatrix.compose(this.scratchVec, IDENTITY_QUAT, ONE_SCALE)
        this.ghostMesh.setMatrixAt(n, this.scratchMatrix)
        this.ghostMesh.setColorAt(
          n,
          g.legal
            ? this.scratchColor.setRGB(0.4, 1, 0.55)
            : blocked
              ? this.scratchColor.setRGB(0.68, 0.12, 0.1)
              : this.scratchColor.setRGB(1, 0.3, 0.25),
        )
        n++
      }
    }
    this.ghostMesh.count = n
    this.ghostMesh.instanceMatrix.needsUpdate = true
    if (this.ghostMesh.instanceColor) this.ghostMesh.instanceColor.needsUpdate = true
  }

  /**
   * Rebuild terrain after the map changes — a wall destroyed, a field depleted.
   * A full rebuild rather than an in-place colour patch, because cliff removal
   * changes the geometry (its side quads disappear), not only its colours.
   */
  refreshTerrain(): void {
    if (this.terrainMesh) {
      this.scene.remove(this.terrainMesh)
      this.terrainMesh.geometry.dispose()
      ;(this.terrainMesh.material as MeshLambertMaterial).dispose()
      this.terrainMesh = null
    }
    this.buildTerrain()
  }

  /**
   * Weapon and destruction effects, driven by the simulation's events.
   *
   * Subscribed here rather than inside `Effects` so that the effect layer stays
   * a pure "draw this" surface with no knowledge of the event vocabulary.
   */
  private subscribeEffects(ctx: Ctx): void {
    ctx.on('weapon:fire', (p) => {
      const q = p as {
        shooter: number
        weapon: number
        owner: number
        x: number
        y: number
        tx: number
        ty: number
      }
      const x = fix.toFloat(fix.raw(q.x))
      const z = fix.toFloat(fix.raw(q.y))
      const ground = this.groundAt(x, z)
      const weapon = weaponById(q.weapon)
      const shooter = this.entities.resolve(q.shooter)
      if (shooter >= 0) this.entityRenderer?.recoil(shooter, this.ctx.time.tick)
      const ramp = (weapon && IMPACT_RAMPS[weapon.key]) ?? 'fire'
      this.effects?.muzzleFlash(fix.raw(q.x), fix.raw(q.y), fix.raw(q.tx), fix.raw(q.ty), ground, ramp)
      // A ballistic weapon has a shell to watch; an instant one has nothing at
      // all between the barrel and the damage, so it gets a beam.
      if (!weapon) return
      if (weapon.projectile === PROJECTILE.Ballistic) {
        // Ordnance leaving the rack. A bomb released from a gunship starts a
        // cell and a half up, and without the separation puff the shell simply
        // appears in mid-air with nothing connecting it to the aircraft.
        const slot = this.entities.resolve(q.shooter)
        const height = slot >= 0 && this.entities.has(slot, EF.Air) ? AIR_ALTITUDE : 0.5
        this.effects?.release(fix.raw(q.x), fix.raw(q.y), ground, height)
        return
      }
      // An energy weapon draws a fat beam in the owner's own colour; a bullet
      // draws a thin hot streak. Same primitive, and the difference is what
      // makes a prism volley look like something other than rifle fire.
      const energy = weapon.warhead === WARHEAD.Beam
      this.effects?.beam(
        fix.raw(q.x),
        fix.raw(q.y),
        fix.raw(q.tx),
        fix.raw(q.ty),
        ground,
        energy ? beamAccent(q.owner) : 'fire',
        // Wider than it was. A tracer used to be 0.06 cells — under two internal
        // pixels — which only ever read because beams stacked: nothing aged them,
        // so four hundred ticks of shots piled along the same lines and summed to
        // a bright rope. One beam on its own was invisible, and once effect time
        // started running that is exactly what the game showed.
        energy ? 0.17 : 0.1,
      )
    })
    // Prism Tanks feeding a shot: a beam from each contributor into the tank
    // that is firing. Drawn before the shot lands, so a charged volley is
    // visibly a volley rather than just a bigger number in the damage log.
    ctx.on('weapon:chain', (p) => {
      const q = p as {
        owner: number
        x: number
        y: number
        links: Array<{ x: number; y: number }>
      }
      const x = fix.toFloat(fix.raw(q.x))
      const z = fix.toFloat(fix.raw(q.y))
      const ground = this.groundAt(x, z)
      const accent = beamAccent(q.owner)
      for (const link of q.links) {
        this.effects?.link(fix.raw(link.x), fix.raw(link.y), fix.raw(q.x), fix.raw(q.y), ground, accent)
      }
    })
    ctx.on('weapon:impact', (p) => {
      const q = p as { weapon: number; x: number; y: number }
      const x = fix.toFloat(fix.raw(q.x))
      const z = fix.toFloat(fix.raw(q.y))
      const weapon = weaponById(q.weapon)
      const ramp = (weapon && IMPACT_RAMPS[weapon.key]) ?? 'fire'
      // A beam carries no splash, so the radius rule would give it the smallest
      // burst in the game — and a tier-three energy weapon that lands quieter
      // than a rifle round is the wrong story about what just happened.
      const scale = ramp === 'fire' ? blastScale(weapon) : Math.max(blastScale(weapon), 1.35)
      this.effects?.explosion(fix.raw(q.x), fix.raw(q.y), this.groundAt(x, z), scale, ramp)
    })
    ctx.on('actor:death', (p) => {
      const q = p as { kind: number; owner: number; x: number; y: number }
      const x = fix.toFloat(fix.raw(q.x))
      const z = fix.toFloat(fix.raw(q.y))
      const ground = this.groundAt(x, z)
      const structure = defById(q.kind)?.isStructure ?? false
      if (structure) this.effects?.wreck(fix.raw(q.x), fix.raw(q.y), ground)
      else this.effects?.explosion(fix.raw(q.x), fix.raw(q.y), ground, 1.4)
      // And the blocks it was made of, in its owner's colour. A building comes
      // apart into more of them than a tank does.
      this.effects?.debris(
        fix.raw(q.x),
        fix.raw(q.y),
        ground,
        this.ownerRgb(q.owner),
        structure ? 1.8 : 1,
      )
    })
  }

  /** An owner's colour as linear RGB, for effects that should look like them. */
  private ownerRgb(owner: number): [number, number, number] {
    const ramps = this.palette.ownerRamps
    const ramp = ramps[owner < 0 ? 0 : owner % ramps.length]
    const [r, g, b] = this.palette.rgb(ramp.indices[Math.min(1, ramp.indices.length - 1)])
    return [r / 255, g / 255, b / 255]
  }

  /**
   * Effects that read state rather than events: shells in flight, aircraft
   * exhaust, and fires on anything badly hurt.
   *
   * Driven from `postFixedUpdate` — once per simulation tick — rather than per
   * frame. Two things follow from that, and both matter. A 144 Hz machine no
   * longer spawns five times the smoke a 30 Hz one does off the same fight; and
   * these emitters run during a headless seek, so a shell that has been in the
   * air for six ticks has a six-tick trail behind it in a captured frame and a
   * base that has been burning for two seconds has two seconds of column over
   * it. Nothing here writes simulation state: it reads positions and health and
   * emits presentation.
   */
  private emitTickEffects(tick: number): void {
    const fx = this.effects
    if (!fx) return

    const combat = this.ctx.has('combat') ? this.ctx.get<CombatView>('combat') : null
    if (combat) {
      for (let i = 0; i < combat.projectileCapacity; i++) {
        const p = combat.projectileAt(i)
        if (!p.active) continue
        const x = fix.toFloat(fix.raw(p.x))
        const z = fix.toFloat(fix.raw(p.y))
        fx.tracer(x, z, this.groundAt(x, z))
      }
    }

    // `alpha` is render-only and reading it on the tick path would be a bug, so
    // these sample the transform this tick just finished: alpha of 1.
    const alpha = 1
    const limit = this.entities.limit
    let dustBudget = 4
    for (let s = 0; s < limit; s++) {
      if (!this.entities.isAlive(s)) continue
      if (fx.visibleAt && !fx.visibleAt(this.transforms.x(s, alpha), this.transforms.y(s, alpha))) continue
      if (dustBudget > 0 && (tick + s) % 9 === 0 && this.entities.has(s, EF.Mobile) &&
          !this.entities.has(s, EF.Air) && defById(this.entities.kind[s])?.tab !== TAB.Infantry &&
          (this.entities.vx[s] !== 0 || this.entities.vy[s] !== 0)) {
        const x = this.transforms.x(s, alpha)
        const z = this.transforms.y(s, alpha)
        fx.movement(x, z, this.groundAt(x, z), this.transforms.heading(s, alpha), tick + s)
        dustBudget--
      }
      if (this.entities.has(s, EF.Air)) {
        // Engine exhaust. An aircraft is the fastest thing on the map and had
        // nothing attached to it at all — the trail is what makes a pass over a
        // position read as a pass rather than as a model sliding sideways.
        //
        // On a stagger, which is what `exhaust` has always documented and never
        // got: it lays a whole trail per call, five puffs spread two cells back,
        // so calling it thirty times a second stacked thirty overlapping copies
        // of the same trail into the one rope of pale gravel that four gunships
        // wrote across the strike frames. Every third tick still overlaps — the
        // aircraft covers well under a puff's spacing in that time — and it stops
        // a flight of four from spending a third of the particle pool on smoke.
        if ((tick + s) % 3 === 0) {
          const ax = this.transforms.x(s, alpha)
          const az = this.transforms.y(s, alpha)
          const ay = this.groundAt(ax, az)
          fx.exhaust(ax, az, ay + AIR_ALTITUDE - 0.1, ay, this.transforms.heading(s, alpha), tick + s)
        }
      }
      const maxHp = this.entities.maxHp[s]
      if (maxHp <= 0) continue
      const health = this.entities.hp[s] / maxHp
      // Damage is visible on the thing that took it, not only on its health bar.
      // A unit under half health smoulders and one nearly dead burns, which is
      // what makes a firing line degrade over a fight instead of looking pristine
      // right up to the frame it disappears. Aircraft are exempt: a trail hanging
      // in the sky under a moving plane reads as a bug, not as damage.
      if (health > 0.55 || this.entities.has(s, EF.Air)) continue
      const structure = this.entities.has(s, EF.Structure)
      // Staggered per slot so a burning base flickers rather than pulsing, and
      // slower the healthier it is: near-dead things smoke twice as often.
      const period = health > 0.3 ? 19 : 11
      if ((tick + s * 7) % period !== 0) continue
      const x = this.transforms.x(s, alpha)
      const z = this.transforms.y(s, alpha)
      fx.fire(
        x,
        z,
        this.groundAt(x, z),
        tick + s,
        1 - health / 0.55,
        structure ? 1.6 : 0.85,
      )
    }
  }

  // --- viewport ------------------------------------------------------------

  resize(width: number, height: number): void {
    const previousTarget = this.camera ? targetForPan(this.basis, this.camera) : null

    this.layout = computeLayout(
      width,
      height - (this.hasHud && width < 700 ? 244 : 0),
      this.hasHud && width >= 700 ? SIDEBAR_WIDTH : 0,
      window.devicePixelRatio || 1,
      this.pixelScale,
    )
    const drawnW = this.layout.internalWidth * this.layout.scale
    const drawnH = this.layout.internalHeight * this.layout.scale

    // The frustum and the world-per-pixel steps depend on the internal target,
    // which now follows the window — so the basis is rebuilt rather than fixed
    // at construction.
    this.basis = new CameraBasis(this.layout.internalWidth, this.layout.internalHeight)
    this.target.setSize(this.layout.internalWidth, this.layout.internalHeight)
    this.orthoCamera.left = -this.basis.halfWidth
    this.orthoCamera.right = this.basis.halfWidth
    this.orthoCamera.top = this.basis.halfHeight
    this.orthoCamera.bottom = -this.basis.halfHeight
    this.orthoCamera.updateProjectionMatrix()

    // The backing store is exactly the upscaled internal target, so the blit is
    // a whole-number nearest-neighbour expansion with no resampling anywhere.
    this.canvas.width = drawnW
    this.canvas.height = drawnH
    this.canvas.style.width = `${drawnW}px`
    this.canvas.style.height = `${drawnH}px`
    this.canvas.style.position = 'absolute'
    this.canvas.style.left = `${this.layout.rect.x + this.layout.offsetX}px`
    this.canvas.style.top = `${this.layout.rect.y + this.layout.offsetY}px`
    this.canvas.style.imageRendering = 'pixelated'
    this.renderer.setSize(drawnW, drawnH, false)

    // Keep looking at whatever was in the middle of the screen before.
    if (previousTarget) this.centerOn(previousTarget.x, previousTarget.z)
  }

  /**
   * Whose fog is drawn. Negative is an observer — the capture harness and a
   * bot-match host — who sees the whole map.
   */
  setLocalSlot(slot: number): void {
    this.fog.localSlot = slot
    if (this.entityRenderer) this.entityRenderer.localSlot = slot
  }



  /** Display pixels per internal pixel. Changing it re-lays-out the canvas. */
  setPixelScale(scale: number): void {
    this.pixelScale = Math.max(1, Math.floor(scale))
    this.resize(window.innerWidth, window.innerHeight)
  }

  get currentPixelScale(): number {
    return this.pixelScale
  }

  private pixelScale = PIXEL_SCALE

  getLayout(): Layout {
    return this.layout
  }

  /** World point the camera is looking at. The audio listener follows it. */
  cameraTarget(): Vector3 {
    return targetForPan(this.basis, this.camera)
  }

  /** The single screen→world conversion. Never derive this elsewhere. */
  screenToWorld(clientX: number, clientY: number, out?: Vector3): Vector3 | null {
    const point = screenToWorldMath(clientX, clientY, this.layout, this.basis, this.camera, out)
    return point ? projectOnSurface(point, this.basis, this.sampleGround, this.surfaceCeiling, -WATER_DROP - 0.1) : null
  }

  /** The single world→screen conversion. */
  worldToScreen(world: Vector3): { x: number; y: number } {
    return worldToScreenMath(world, this.layout, this.basis, this.camera)
  }

  entityBounds(slot: number): { left: number; top: number; right: number; bottom: number } | null {
    if (!this.entityRenderer?.canSeeSlot(slot)) return null
    const geometry = this.geometryFor(this.entities.kind[slot])
    if (!geometry.boundingBox) geometry.computeBoundingBox()
    const bounds = geometry.boundingBox!
    const structure = this.entities.has(slot, EF.Structure)
    const scale = structure ? 1 : scaleFor(this.entities.kind[slot]).x
    const alpha = this.ctx.time.alpha
    const x = this.transforms.x(slot, alpha)
    const z = this.transforms.y(slot, alpha)
    const y = this.groundAt(x, z) + (this.entities.has(slot, EF.Air) ? AIR_ALTITUDE : 0)
    const yaw = structure ? 0 : Math.PI / 2 - this.transforms.heading(slot, alpha)
    const c = Math.cos(yaw)
    const s = Math.sin(yaw)
    const box = { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity }
    for (let corner = 0; corner < 8; corner++) {
      const px = (corner & 1 ? bounds.max.x : bounds.min.x) * scale
      const py = (corner & 2 ? bounds.max.y : bounds.min.y) * scale
      const pz = (corner & 4 ? bounds.max.z : bounds.min.z) * scale
      const screen = this.worldToScreen(this.scratchVec.set(x + px * c + pz * s, y + py, z + pz * c - px * s))
      box.left = Math.min(box.left, screen.x)
      box.right = Math.max(box.right, screen.x)
      box.top = Math.min(box.top, screen.y)
      box.bottom = Math.max(box.bottom, screen.y)
    }
    return box
  }

  /** Pan by whole internal pixels, clamped to the map. */
  panBy(dxPixels: number, dyPixels: number): void {
    const next = clampPan(this.basis, {
      panX: this.camera.panX + dxPixels,
      panY: this.camera.panY + dyPixels,
    })
    this.camera.panX = Math.round(next.panX)
    this.camera.panY = Math.round(next.panY)
  }

  /** Centre the view on a world cell. */
  centerOn(cellX: number, cellY: number): void {
    const base = new Vector3(this.world.size / 2, 0, this.world.size / 2)
    const rel = new Vector3(cellX - base.x, 0, cellY - base.z)
    const a = this.basis.stepRight
    const b = this.basis.stepUp
    const aa = a.x * a.x + a.z * a.z
    const ab = a.x * b.x + a.z * b.z
    const bb = b.x * b.x + b.z * b.z
    const ra = rel.x * a.x + rel.z * a.z
    const rb = rel.x * b.x + rel.z * b.z
    const det = aa * bb - ab * ab
    const next = clampPan(this.basis, {
      panX: Math.round((ra * bb - rb * ab) / det),
      panY: Math.round((rb * aa - ra * ab) / det),
    })
    this.camera.panX = next.panX
    this.camera.panY = next.panY
  }

  /** Draw calls issued for entities last frame. Read by the profiler. */
  get entityDrawCalls(): number {
    return this.entityRenderer?.drawCalls ?? 0
  }

  /** Triangles submitted for entities last frame. */
  get entityTriangles(): number {
    return this.entityRenderer?.triangles ?? 0
  }

  /**
   * Shader programs compiled so far.
   *
   * The performance gate asserts this stops rising after boot: the reference
   * project lost 728–1236 ms frames to shaders compiling mid-match, and a
   * static benchmark reported 94 fps throughout.
   */
  get programCount(): number {
    return this.renderer.info.programs?.length ?? 0
  }

  /** The WebGL renderer, so the icon forge can share this context. */
  get webglRenderer(): WebGLRenderer {
    return this.renderer
  }

  /**
   * Geometry for an entity kind, for the icon forge (`IconSource`).
   *
   * Placeholder boxes sized from the registry footprint until U15's mesh
   * recipes land; the icon pipeline itself does not change when they do.
   */
  geometryFor(kind: number): BufferGeometry {
    const cached = this.iconGeometry.get(kind)
    if (cached) return cached
    const recipe = recipeFor(kind)
    const merged = new BufferGeometry()
    if (recipe && recipe.parts.length > 0) {
      // Merge every sub-part so the icon shows the whole entity rather than
      // just its hull — an icon of a turretless tank is not a tank.
      const parts = previewGeometries(recipe)
      const geometry = mergeParts(parts)
      merged.copy(geometry)
      geometry.dispose()
      for (const part of parts) part.dispose()
    } else {
      const def = defById(kind)
      merged.copy(boxGeometry(def?.width ?? 1, 0.6, def?.height ?? 1))
    }
    this.iconGeometry.set(kind, merged)
    return merged
  }

  private readonly iconGeometry = new Map<number, BufferGeometry>()

  /**
   * Render each registry entity alone and reduce it to a binary mask (U19).
   *
   * The mask is the thing that actually has to be recognisable: at this zoom a
   * unit is a handful of pixels, so what identifies it is the outline. The
   * critic's identification test consumes these.
   */
  captureSilhouettes(): Array<{ key: string; width: number; height: number; mask: number[] }> {
    const size = 48
    const target = new WebGLRenderTarget(size, size, {
      minFilter: NearestFilter,
      magFilter: NearestFilter,
    })
    const scene = new Scene()
    const light = new DirectionalLight(0xffffff, 3.4)
    light.position.set(0.5, 0.8, 0.35).normalize()
    scene.add(light)
    scene.add(new AmbientLight(0xffffff, 2))

    const camera = new OrthographicCamera(-1.5, 1.5, 1.5, -1.5, 0.01, 40)
    camera.position.set(2.2, 2.6, 2.2)
    camera.lookAt(new Vector3(0, 0.3, 0))

    const pixels = new Uint8Array(size * size * 4)
    const out: Array<{ key: string; width: number; height: number; mask: number[] }> = []
    const material = new MeshLambertMaterial({ color: 0xffffff, flatShading: true })

    const previous = this.renderer.getRenderTarget()
    for (const def of ENTITY_DEFS) {
      const recipe = recipeFor(def.id)
      if (!recipe) continue
      const meshes: Mesh[] = []
      for (const geometry of previewGeometries(recipe)) {
        const mesh = new Mesh(geometry, material)
        const span = Math.max(def.width, def.height, 1)
        mesh.scale.setScalar(1.7 / span)
        scene.add(mesh)
        meshes.push(mesh)
      }

      this.renderer.setRenderTarget(target)
      this.renderer.setClearColor(0x000000, 1)
      this.renderer.clear()
      this.renderer.render(scene, camera)
      this.renderer.readRenderTargetPixels(target, 0, 0, size, size, pixels)

      const mask: number[] = new Array(size * size)
      for (let i = 0; i < size * size; i++) {
        // Anything lit at all is the entity; the background is pure black.
        mask[i] = pixels[i * 4] + pixels[i * 4 + 1] + pixels[i * 4 + 2] > 12 ? 1 : 0
      }
      out.push({ key: def.key, width: size, height: size, mask })

      for (const mesh of meshes) { scene.remove(mesh); mesh.geometry.dispose() }
    }

    this.renderer.setRenderTarget(previous)
    this.renderer.setClearColor(0x0a0705, 1)
    material.dispose()
    target.dispose()
    return out
  }

  /** Read the internal target back as raw RGBA. Used by the capture harness. */
  readInternalPixels(): Uint8Array {
    const w = this.layout.internalWidth
    const h = this.layout.internalHeight
    const buf = new Uint8Array(w * h * 4)
    this.renderer.readRenderTargetPixels(this.target, 0, 0, w, h, buf)
    return buf
  }

  dispose(): void {
    for (const g of this.iconGeometry.values()) g.dispose()
    this.iconGeometry.clear()
    this.bars?.dispose()
    this.fog.dispose()
    this.pixel.dispose()
    this.target.dispose()
    this.terrainMesh?.geometry.dispose()
    ;(this.terrainMesh?.material as MeshLambertMaterial | undefined)?.dispose()
    if (this.scatterMesh) {
      this.scene.remove(this.scatterMesh)
      this.scatterMesh.geometry.dispose()
      this.scatterMesh.material.dispose()
      this.scatterMesh = null
    }
    this.entityRenderer?.dispose()
    this.routeMesh?.geometry.dispose()
    ;(this.routeMesh?.material as LineDashedMaterial | undefined)?.dispose()
    for (const m of [this.ringMesh, this.markerMesh, this.ghostMesh]) {
      if (!m) continue
      m.geometry.dispose()
      ;(m.material as MeshBasicMaterial).dispose()
      m.dispose()
    }
    this.renderer.dispose()
  }
}

const ONE_SCALE = new Vector3(1, 1, 1)
const IDENTITY_QUAT = new Quaternion()

/**
 * Concatenate several geometries into one. Only used for icons, where a single
 * draw is simpler than replaying the sub-part hierarchy in a 44-pixel frame.
 */
function mergeParts(parts: BufferGeometry[]): BufferGeometry {
  const out = new BufferGeometry()
  const positions: number[] = []
  const normals: number[] = []
  const shades: number[] = []
  const panels: number[] = []
  const indices: number[] = []
  for (const g of parts) {
    const base = positions.length / 3
    const p = g.getAttribute('position')
    const n = g.getAttribute('normal')
    const sh = g.getAttribute('shade')
    const panel = g.getAttribute('panel')
    for (let i = 0; i < p.count; i++) {
      positions.push(p.getX(i), p.getY(i), p.getZ(i))
      normals.push(n.getX(i), n.getY(i), n.getZ(i))
      shades.push(sh ? sh.getX(i) : 0)
      panels.push(panel.getX(i), panel.getY(i), panel.getZ(i), panel.getW(i))
    }
    const idx = g.getIndex()
    if (idx) for (let i = 0; i < idx.count; i++) indices.push(idx.getX(i) + base)
  }
  out.setAttribute('position', new BufferAttribute(Float32Array.from(positions), 3))
  out.setAttribute('normal', new BufferAttribute(Float32Array.from(normals), 3))
  out.setAttribute('shade', new BufferAttribute(Float32Array.from(shades), 1))
  out.setAttribute('panel', new BufferAttribute(Float32Array.from(panels), 4))
  out.setIndex(indices)
  out.computeBoundingSphere()
  return out
}

/** A flat square tile on the ground plane, for the placement ghost. */
function tileGeometry(size: number): BufferGeometry {
  const h = size / 2
  const g = new BufferGeometry()
  g.setAttribute(
    'position',
    new BufferAttribute(Float32Array.from([-h, 0, -h, h, 0, -h, h, 0, h, -h, 0, h]), 3),
  )
  g.setIndex([0, 2, 1, 0, 3, 2])
  g.computeVertexNormals()
  return g
}

/** Open corners leave the unit visible inside its selection marker. */
function selectionGeometry(): BufferGeometry {
  const positions: number[] = []
  const indices: number[] = []
  for (const x of [-1, 1]) {
    for (const z of [-1, 1]) {
      for (const axis of [0, 1]) {
        const x0 = axis === 0 ? Math.min(x, x * 0.62) : x - 0.018
        const x1 = axis === 0 ? Math.max(x, x * 0.62) : x + 0.018
        const z0 = axis === 1 ? Math.min(z, z * 0.62) : z - 0.018
        const z1 = axis === 1 ? Math.max(z, z * 0.62) : z + 0.018
        const base = positions.length / 3
        positions.push(x0, 0, z1, x1, 0, z1, x1, 0, z0, x0, 0, z0)
        indices.push(base, base + 1, base + 2, base, base + 2, base + 3)
      }
    }
  }
  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(Float32Array.from(positions), 3))
  geometry.setIndex(indices)
  return geometry
}

/** A flat diamond on the ground plane, used for order markers. */
function diamondGeometry(radius: number, innerRatio = 0.62): BufferGeometry {
  const g = new BufferGeometry()
  // A ratio, not a radius. An inner edge at or beyond the outer one turns the
  // band inside out and the whole ring disappears, so it is clamped rather than
  // left to the caller to remember.
  const inner = radius * Math.min(Math.max(innerRatio, 0.05), 0.95)
  const p: number[] = []
  const idx: number[] = []
  const pts = [
    [radius, 0],
    [0, radius],
    [-radius, 0],
    [0, -radius],
  ]
  const innerPts = [
    [inner, 0],
    [0, inner],
    [-inner, 0],
    [0, -inner],
  ]
  for (let i = 0; i < 4; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % 4]
    const ai = innerPts[i]
    const bi = innerPts[(i + 1) % 4]
    const base = p.length / 3
    p.push(a[0], 0, a[1], b[0], 0, b[1], bi[0], 0, bi[1], ai[0], 0, ai[1])
    // Wound so the face points **up**. The obvious ordering produces a normal
    // of -Y here, which back-face culling then removes entirely — the ring is
    // built, positioned, and completely invisible.
    idx.push(base, base + 2, base + 1, base, base + 3, base + 2)
  }
  g.setAttribute('position', new BufferAttribute(Float32Array.from(p), 3))
  g.setIndex(idx)
  g.computeVertexNormals()
  return g
}

/** A unit box, built rather than imported, because BoxGeometry is fine but explicit is clearer. */
function boxGeometry(w: number, h: number, d: number): BufferGeometry {
  const hw = w / 2
  const hd = d / 2
  const g = new BufferGeometry()
  const p: number[] = []
  const n: number[] = []
  const idx: number[] = []
  const face = (verts: number[][], normal: number[]) => {
    const base = p.length / 3
    for (const v of verts) {
      p.push(v[0], v[1], v[2])
      n.push(normal[0], normal[1], normal[2])
    }
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3)
  }
  face(
    [
      [-hw, h, -hd],
      [hw, h, -hd],
      [hw, h, hd],
      [-hw, h, hd],
    ],
    [0, 1, 0],
  )
  face(
    [
      [-hw, 0, hd],
      [hw, 0, hd],
      [hw, h, hd],
      [-hw, h, hd],
    ],
    [0, 0, 1],
  )
  face(
    [
      [hw, 0, -hd],
      [-hw, 0, -hd],
      [-hw, h, -hd],
      [hw, h, -hd],
    ],
    [0, 0, -1],
  )
  face(
    [
      [hw, 0, hd],
      [hw, 0, -hd],
      [hw, h, -hd],
      [hw, h, hd],
    ],
    [1, 0, 0],
  )
  face(
    [
      [-hw, 0, -hd],
      [-hw, 0, hd],
      [-hw, h, hd],
      [-hw, h, -hd],
    ],
    [-1, 0, 0],
  )
  g.setAttribute('position', new BufferAttribute(Float32Array.from(p), 3))
  g.setAttribute('normal', new BufferAttribute(Float32Array.from(n), 3))
  g.setIndex(idx)
  return g
}
